import {
  FlexRow,
  MaterialIcon,
  StyledButton,
  StyledSearchBar,
  useDebouncedSearch,
  useModal,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import useEffectOnce from '../../hooks/useEffectOnce';
import { useStyles } from '../../hooks/useStyles';
import {
  userTypeFilterState,
  usersClientIdFilterState,
  usersSearchFilterState,
  usersSubAccountIdFilterState,
  usersSubClientIdFilterState,
} from '../../storage/users';
import StyledTooltip from '../ui/StyledTooltip';
import { UserFilters } from './UserFilters';
import UserDetailModal from './modal/UserDetailModal';
import UserFiltersModal from './modal/UserFiltersModal';

type Props = {
  onReloadPage: () => void;
};
export function UserFiltersBar({ onReloadPage }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const { openModal: openFilterModal, closeModal: closeFilterModal, showModal: showFilterModal } = useModal();
  const { openModal: openFormModal, closeModal: closeFormModal, showModal: showFormModal } = useModal();
  const { isLarge } = useBreakpoints();
  const { search, setSearch } = useDebouncedSearch(useRecoilState(usersSearchFilterState));
  const resetSearch = useResetRecoilState(usersSearchFilterState);
  const resetSelectedUserType = useResetRecoilState(userTypeFilterState);
  const resetSelectedClientId = useResetRecoilState(usersClientIdFilterState);
  const resetSelectedSubClientId = useResetRecoilState(usersSubClientIdFilterState);
  const resetSelectedSubAccountId = useResetRecoilState(usersSubAccountIdFilterState);

  useEffectOnce(() => {
    return () => {
      resetFilters();
    };
  });

  const resetFilters = () => {
    resetSearch();
    resetSelectedUserType();
    resetSelectedClientId();
    resetSelectedSubClientId();
    resetSelectedSubAccountId();
  };

  return (
    <FlexRow
      classNames={css({
        justifyContent: 'space-between',
        width: '100%',
      })}
    >
      <FlexRow
        gap={theme.sizing.scale300}
        classNames={css({
          width: '70%',
        })}
      >
        <FlexRow
          classNames={css({
            width: '60%',
          })}
        >
          <StyledSearchBar
            value={search ?? ''}
            onChange={setSearch}
            clearable
            noMargin
          />
        </FlexRow>
        {!isLarge && <UserFilters />}
        {isLarge && (
          <StyledTooltip
            content={'Filtros'}
            showArrow={false}
          >
            <Button
              size="compact"
              shape="square"
              onClick={openFilterModal}
              kind="tertiary"
            >
              <MaterialIcon name={'filter_list'} />
            </Button>
          </StyledTooltip>
        )}
        <StyledTooltip
          content={'Restaurar filtros'}
          showArrow={false}
        >
          <Button
            size="compact"
            shape="square"
            onClick={resetFilters}
            kind="tertiary"
          >
            <MaterialIcon name={'autorenew'} />
          </Button>
        </StyledTooltip>
      </FlexRow>
      <FlexRow gap={theme.sizing.scale300}>
        <StyledTooltip
          content={'Recargar tabla'}
          showArrow={false}
        >
          <Button
            size="compact"
            shape="square"
            onClick={onReloadPage}
            kind="tertiary"
          >
            <MaterialIcon name={'refresh'} />
          </Button>
        </StyledTooltip>
        <StyledButton
          onClick={openFormModal}
          startEnhancer={
            <MaterialIcon
              name="add"
              size="xs-mini"
              color={theme.colors.contentInversePrimary}
            />
          }
        >
          Nuevo
        </StyledButton>
      </FlexRow>
      {showFilterModal && (
        <UserFiltersModal
          onClose={closeFilterModal}
          isOpen={showFilterModal}
        />
      )}
      {showFormModal && (
        <UserDetailModal
          onClose={(updateTable) => {
            if (updateTable) {
              onReloadPage();
            }
            closeFormModal();
          }}
          isOpen={showFormModal}
        />
      )}
    </FlexRow>
  );
}
